import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Key,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

interface SoftwareLicense {
  id: string;
  name: string;
  vendor: string;
  licenseKey?: string;
  licenseType: string;
  seats: number;
  seatsUsed: number;
  purchaseDate?: string;
  expirationDate?: string;
  cost?: string;
  status: string;
  notes?: string;
}

export default function LicensesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState<SoftwareLicense | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['licenses', { page, search, status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      const response = await api.get(`/licenses?${params.toString()}`);
      return response.data;
    },
  });

  const licenses: SoftwareLicense[] = data?.data || [];
  const pagination = data?.pagination;
  const stats = data?.stats;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/licenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      toast({ title: 'License deleted successfully' });
    },
  });

  const getStatusBadge = (license: SoftwareLicense) => {
    if (license.status === 'EXPIRED') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Expired</span>;
    }
    if (license.expirationDate) {
      const daysUntilExpiry = Math.ceil((new Date(license.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Expiring Soon</span>;
      }
    }
    if (license.seatsUsed >= license.seats) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">Full</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Active</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Software Licenses</h1>
          <p className="text-muted-foreground">Track and manage software licenses and compliance</p>
        </div>
        <PermissionGate permission="licenses.create">
          <Button onClick={() => { setEditingLicense(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add License
          </Button>
        </PermissionGate>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Licenses</p>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{stats?.active || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-2xl font-bold">{stats?.expiringSoon || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="text-2xl font-bold">{stats?.expired || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search licenses..."
                className="pl-10"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRING_SOON">Expiring Soon</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : licenses.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No licenses found</p>
              <Button variant="link" onClick={() => setShowModal(true)}>
                Add your first license
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Software</th>
                    <th className="text-left p-4 font-medium">Vendor</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Seats</th>
                    <th className="text-left p-4 font-medium">Expiration</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {licenses.map((license) => (
                    <tr key={license.id} className="hover:bg-muted/50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{license.name}</p>
                          {license.licenseKey && (
                            <p className="text-sm text-muted-foreground font-mono">
                              {license.licenseKey.substring(0, 8)}...
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">{license.vendor}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-xs rounded bg-muted">
                          {license.licenseType}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "font-medium",
                            license.seatsUsed >= license.seats && "text-red-600"
                          )}>
                            {license.seatsUsed}
                          </span>
                          <span className="text-muted-foreground">/ {license.seats}</span>
                        </div>
                        <div className="w-20 h-1.5 bg-muted rounded-full mt-1">
                          <div 
                            className={cn(
                              "h-full rounded-full",
                              license.seatsUsed / license.seats > 0.9 ? "bg-red-500" :
                              license.seatsUsed / license.seats > 0.7 ? "bg-yellow-500" : "bg-green-500"
                            )}
                            style={{ width: `${Math.min(100, (license.seatsUsed / license.seats) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        {license.expirationDate ? (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span>{formatDate(license.expirationDate)}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Perpetual</span>
                        )}
                      </td>
                      <td className="p-4">{getStatusBadge(license)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <PermissionGate permission="licenses.update">
                            <Button variant="ghost" size="icon" onClick={() => { setEditingLicense(license); setShowModal(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="licenses.delete">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                if (confirm('Delete this license?')) {
                                  deleteMutation.mutate(license.id);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {page} of {pagination.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <LicenseModal
          license={editingLicense}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
          }}
        />
      )}
    </div>
  );
}

interface LicenseModalProps {
  license: SoftwareLicense | null;
  onClose: () => void;
  onSuccess: () => void;
}

function LicenseModal({ license, onClose, onSuccess }: LicenseModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: license?.name || '',
    vendor: license?.vendor || '',
    licenseKey: license?.licenseKey || '',
    licenseType: license?.licenseType || 'PERPETUAL',
    seats: license?.seats || 1,
    purchaseDate: license?.purchaseDate?.split('T')[0] || '',
    expirationDate: license?.expirationDate?.split('T')[0] || '',
    cost: license?.cost || '',
    notes: license?.notes || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (license) {
        await api.put(`/licenses/${license.id}`, formData);
        toast({ title: 'License updated successfully' });
      } else {
        await api.post('/licenses', formData);
        toast({ title: 'License created successfully' });
      }
      onSuccess();
    } catch (error) {
      toast({ title: 'Failed to save license', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{license ? 'Edit License' : 'Add License'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Software Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Vendor *</label>
                <Input
                  required
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">License Type</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.licenseType}
                  onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                >
                  <option value="PERPETUAL">Perpetual</option>
                  <option value="SUBSCRIPTION">Subscription</option>
                  <option value="VOLUME">Volume</option>
                  <option value="OEM">OEM</option>
                  <option value="SITE">Site License</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">License Key</label>
                <Input
                  value={formData.licenseKey}
                  onChange={(e) => setFormData({ ...formData, licenseKey: e.target.value })}
                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Number of Seats</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.seats}
                  onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Purchase Date</label>
                <Input
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Expiration Date</label>
                <Input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[80px]"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (license ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
